import React, { useContext, useEffect, useReducer, useRef, useState } from 'react';
import { useNavigate, useLocation, Link, Navigate } from 'react-router-dom';
import { ArrowLeft, AlertTriangle, Loader2 } from 'lucide-react';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { getError } from '../../utils';
import { Store } from '../../Store';
import reservaService from '../../services/reserva.service';
import compradorService from '../../services/comprador.service';
import settingsService from '../../services/settings.service';
import CheckoutStepper from '../../components/checkout/CheckoutStepper';
import AccountChoice from '../../components/checkout/AccountChoice';
import CuentaDestinoField, {
  useCuentasDestino,
} from '../../components/checkout/CuentaDestinoField';
import { paths } from '../../routes/paths';
import { getAnalyticsAttribution, trackAnalyticsEvent } from '../../lib/analytics';

const reducer = (state, action) => {
  switch (action.type) {
    case 'CREATE_REQUEST':
      return { ...state, loadingCreate: true };
    case 'CREATE_SUCCESS':
      return { ...state, loadingCreate: false };
    case 'CREATE_FAIL':
      return { ...state, loadingCreate: false };
    default:
      return state;
  }
};

function Field({ label, error, children }) {
  return (
    <div className='space-y-1'>
      <label className='block text-sm font-medium text-ink'>{label}</label>
      {children}
      {error && <p className='text-xs text-red-500'>{error}</p>}
    </div>
  );
}

function Input({ ...props }) {
  return (
    <input
      className='w-full rounded-xl border border-divider bg-white px-3 py-2.5 text-sm text-ink placeholder:text-zinc-400 focus:border-ink focus:outline-none focus:ring-1 focus:ring-ink/20'
      {...props}
    />
  );
}

function resolveRifaInfo(tickets, rifaId, rifaNombre) {
  if (rifaId) {
    return { _id: rifaId, nombre: rifaNombre || '' };
  }
  const rifa = tickets[0]?.rifa;
  if (!rifa) return null;
  if (typeof rifa === 'object' && rifa._id) return rifa;
  return { _id: String(rifa), nombre: rifaNombre || '' };
}

function readCheckoutPayload(location) {
  if (location.state?.tickets?.length) {
    return {
      tickets: location.state.tickets,
      rifaId: location.state.rifaId,
      rifaNombre: location.state.rifaNombre,
    };
  }
  try {
    const raw = sessionStorage.getItem('pendingCheckout');
    if (raw) {
      const pending = JSON.parse(raw);
      if (
        (pending.returnTo === paths.reserve || pending.returnTo === '/reservarTickets') &&
        pending.tickets?.length
      ) {
        sessionStorage.removeItem('pendingCheckout');
        return {
          tickets: pending.tickets,
          rifaId: pending.rifaId,
          rifaNombre: pending.rifaNombre,
        };
      }
    }
  } catch {
    /* ignore */
  }
  return { tickets: [], rifaId: null, rifaNombre: '' };
}

export default function NuevaReserva() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { state } = useContext(Store);
  const { userInfo } = state;

  const [{ loadingCreate }, dispatch] = useReducer(reducer, {
    loadingCreate: false,
  });

  /* ── Restore tickets: location.state first, then sessionStorage ── */
  const stateRef = useRef(location.state);
  const initialCheckoutRef = useRef(null);
  if (!initialCheckoutRef.current) {
    initialCheckoutRef.current = stateRef.current?.tickets?.length
      ? {
          tickets: stateRef.current.tickets,
          rifaId: stateRef.current.rifaId,
          rifaNombre: stateRef.current.rifaNombre,
        }
      : readCheckoutPayload(location);
  }
  const initialCheckout = initialCheckoutRef.current;

  const [tickets] = useState(() => initialCheckout.tickets);
  // Bonos with hidden numbers send placeholder tickets (quantity only; the server assigns them)
  const [numerosOcultos] = useState(() => tickets.some(t => t.oculto));
  const [ticketNumbers] = useState(() =>
    tickets
      .map(t => t.nroSerial)
      .filter(Boolean)
      .join(' · ')
  );
  const [precioTotal] = useState(() => tickets.reduce((acc, t) => acc + (t.precioTotal || 0), 0));
  const [ticketIds] = useState(() => tickets.map(t => t._id));
  const [rifaInfo] = useState(() =>
    resolveRifaInfo(tickets, initialCheckout.rifaId, initialCheckout.rifaNombre)
  );

  /* Payload restored from sessionStorage (login/signup return): move it into history state so
     the selected tickets also survive a page reload. */
  useEffect(() => {
    if (!location.state?.tickets?.length && tickets.length) {
      navigate(location.pathname, {
        replace: true,
        state: { tickets, rifaId: rifaInfo?._id, rifaNombre: rifaInfo?.nombre },
      });
    }
  }, []);

  useEffect(() => {
    if (rifaInfo?._id)
      trackAnalyticsEvent('checkout_start', {
        rifaId: rifaInfo._id,
        ticketCount: tickets.length,
        stage: 'details',
      });
  }, [rifaInfo, tickets]);

  /* ── Account mode: null = not decided, 'guest', 'user' ── */
  const [accountMode, setAccountMode] = useState(userInfo ? 'user' : null);

  /* ── Form state – pre-fill from userInfo when logged in ── */
  const [nombre, setNombre] = useState(userInfo?.nombre || '');
  const [email, setEmail] = useState(userInfo?.email || '');
  const [confirmEmail, setConfirmEmail] = useState(userInfo?.email || '');
  const [cedula, setCedula] = useState('');
  const [telefono, setTelefono] = useState(userInfo?.telefono || '');
  const [nombreVendedor, setNombreVendedor] = useState('');
  const {
    cuentas,
    selected: cuentaDestino,
    setSelected: setCuentaDestino,
    payload: cuentaPayload,
  } = useCuentasDestino();
  const [sellerField, setSellerField] = useState({ label: '', placeholder: '' });

  useEffect(() => {
    settingsService
      .getPublicSettings()
      .then(s =>
        setSellerField({
          label: s?.sellerFieldLabel || '',
          placeholder: s?.sellerFieldPlaceholder || '',
        })
      )
      .catch(() => {});
  }, []);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);

  /* ── Guard ── */
  if (!tickets.length) {
    toast.error(t('ticket.noTicketsSelected', 'No hay tickets seleccionados'));
    return <Navigate to='/' replace />;
  }

  const validate = () => {
    const errs = {};
    if (!nombre.trim()) errs.nombre = t('reservation.enterName');
    if (!email.trim()) errs.email = t('reservation.enterValidEmail');
    if (email !== confirmEmail) errs.confirmEmail = t('reservation.emailsMustMatch');
    if (!cedula.trim()) errs.cedula = t('reservation.enterDocument');
    if (!termsAccepted) errs.terms = t('terms.mustAccept') || 'Debés aceptar los términos';
    return errs;
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setSubmitted(true);
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    try {
      dispatch({ type: 'CREATE_REQUEST' });
      const compradorPayload = {
        nombre: nombre.trim(),
        email: email.trim(),
        cedula: cedula.trim(),
      };
      if (telefono.trim()) compradorPayload.telefono = telefono.trim();

      const compradorData = await compradorService.createComprador(compradorPayload);
      const rifaId =
        rifaInfo?._id ||
        (typeof tickets[0]?.rifa === 'object' ? tickets[0]?.rifa?._id : tickets[0]?.rifa);
      if (!rifaId) throw new Error('No se pudo identificar el bono de los tickets seleccionados');

      const analytics = await getAnalyticsAttribution();
      const data = await reservaService.createReserva({
        comprador: compradorData,
        ...(numerosOcultos ? { tickets: [], cantidad: tickets.length } : { tickets: ticketIds }),
        rifa: rifaId,
        fechaReserva: new Date(),
        analytics,
        ...cuentaPayload,
        ...(nombreVendedor.trim() ? { nombreVendedor: nombreVendedor.trim() } : {}),
      });
      if (!data?._id) throw new Error('Respuesta inválida del servidor');
      trackAnalyticsEvent('reservation_created', {
        rifaId,
        ticketCount: tickets.length,
        stage: 'payment',
      });
      dispatch({ type: 'CREATE_SUCCESS' });
      navigate(paths.checkout(data._id), { replace: true });
    } catch (err) {
      dispatch({ type: 'CREATE_FAIL' });
      toast.error(getError(err));
    }
  };

  const handleGoBack = () => {
    const rifaId = rifaInfo?._id;
    if (rifaId) {
      navigate(paths.buyTickets(rifaId), {
        state: { selectedTickets: tickets },
        replace: true,
      });
    } else {
      navigate(-1);
    }
  };

  /* ── Navigate to sign-in / sign-up, saving tickets in sessionStorage ── */
  const goToAuth = authPath => {
    sessionStorage.setItem(
      'pendingCheckout',
      JSON.stringify({
        tickets,
        rifaId: rifaInfo?._id,
        rifaNombre: rifaInfo?.nombre,
        returnTo: paths.reserve,
      })
    );
    navigate(`${authPath}?returnTo=${encodeURIComponent(paths.reserve)}`);
  };

  const handleGuest = () => {
    setAccountMode('guest');
    setNombre('');
    setEmail('');
    setConfirmEmail('');
    setTelefono('');
  };

  /* Shared submit button */
  const submitButton = (extraClass = '') => (
    <button
      type='submit'
      form='buyer-form'
      disabled={loadingCreate}
      className={`flex w-full items-center justify-center gap-2 rounded-xl bg-ink px-4 py-3 font-semibold text-white transition-opacity hover:opacity-90 active:opacity-80 disabled:opacity-50 ${extraClass}`}
    >
      {loadingCreate ? (
        <>
          <Loader2 className='h-4 w-4 animate-spin' />
          Procesando…
        </>
      ) : (
        'Continuar al pago →'
      )}
    </button>
  );

  return (
    <div className='min-h-screen bg-white font-sans text-ink'>
      {/* ─── Top bar ─────────────────────────────────────────── */}
      <div className='flex items-center gap-3 px-4 py-4 lg:px-8 lg:py-5'>
        <button
          onClick={handleGoBack}
          className='flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-surface'
          aria-label='Volver'
        >
          <ArrowLeft className='h-4 w-4' />
        </button>
        <span className='text-base font-semibold lg:text-lg'>
          {accountMode ? 'Tus datos' : t('checkout.title', 'Finalizar compra')}
        </span>
      </div>

      {/* ─── Main content ────────────────────────────────────── */}
      <div className='mx-auto max-w-7xl px-4 pb-28 lg:px-8 lg:pb-12'>
        {/* Stepper */}
        <div className='mb-6'>
          <CheckoutStepper currentStep={3} />
        </div>

        <div className='lg:grid lg:grid-cols-5 lg:items-start lg:gap-10'>
          {/* ── Left column: order summary ── */}
          <div className='lg:col-span-2 lg:sticky lg:top-24 space-y-4'>
            <div className='rounded-xl bg-surface px-4 py-3 space-y-2'>
              <p className='text-sm font-semibold'>{rifaInfo?.nombre}</p>
              {ticketNumbers && !numerosOcultos && (
                <p className='text-sm text-zinc-500'>
                  Números: <span className='font-medium text-ink'>{ticketNumbers}</span>
                </p>
              )}
              {numerosOcultos && (
                <p className='text-sm text-zinc-500'>
                  {t('ticket.hiddenNumbersQuantity', { count: tickets.length })}
                </p>
              )}
              {precioTotal > 0 && (
                <div className='flex justify-between border-t border-divider pt-2 text-sm font-bold'>
                  <span>Total</span>
                  <span>${precioTotal}</span>
                </div>
              )}
            </div>

            {/* Desktop submit (only when form is shown) */}
            {accountMode && <div className='hidden lg:block'>{submitButton()}</div>}
          </div>

          {/* ── Right column ── */}
          <div className='lg:col-span-3 mt-5 lg:mt-0'>
            {/* Account choice */}
            {!accountMode && (
              <AccountChoice
                t={t}
                onGuest={handleGuest}
                onSignIn={() => goToAuth(paths.signIn)}
                onSignUp={() => goToAuth(paths.signUp)}
              />
            )}

            {/* Buyer form */}
            {accountMode && (
              <form id='buyer-form' onSubmit={handleSubmit} noValidate className='space-y-4'>
                <Field label='Nombre completo' error={submitted ? errors.nombre : undefined}>
                  <Input
                    type='text'
                    placeholder='Juan García'
                    value={nombre}
                    onChange={e => setNombre(e.target.value)}
                    required
                  />
                </Field>

                <Field label='Correo electrónico' error={submitted ? errors.email : undefined}>
                  <Input
                    type='email'
                    placeholder='juan@ejemplo.com'
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    required
                  />
                </Field>

                <Field
                  label='Confirmar correo electrónico'
                  error={submitted ? errors.confirmEmail : undefined}
                >
                  <Input
                    type='email'
                    placeholder='juan@ejemplo.com'
                    value={confirmEmail}
                    onChange={e => setConfirmEmail(e.target.value)}
                    required
                  />
                </Field>

                <Field label='Teléfono (opcional)'>
                  <Input
                    type='tel'
                    placeholder='+598 9X XXX XXX'
                    value={telefono}
                    onChange={e => setTelefono(e.target.value)}
                  />
                </Field>

                <Field
                  label={t('reservation.documentLabel', 'Documento de identidad')}
                  error={submitted ? errors.cedula : undefined}
                >
                  <Input
                    type='text'
                    placeholder={t('reservation.documentPlaceholder', 'Pasaporte, DNI, cédula…')}
                    value={cedula}
                    onChange={e => setCedula(e.target.value)}
                    required
                  />
                </Field>

                <Field label={sellerField.label || t('reservation.sellerNameOptional')}>
                  <Input
                    type='text'
                    placeholder={sellerField.placeholder || t('reservation.sellerNamePlaceholder')}
                    value={nombreVendedor}
                    onChange={e => setNombreVendedor(e.target.value)}
                  />
                  <p className='text-xs text-zinc-400 mt-1'>{t('reservation.sellerNameHint')}</p>
                </Field>

                <CuentaDestinoField
                  t={t}
                  cuentas={cuentas}
                  value={cuentaDestino}
                  onChange={setCuentaDestino}
                />

                {/* Terms */}
                <div className='flex items-start gap-3'>
                  <input
                    id='terms'
                    type='checkbox'
                    checked={termsAccepted}
                    onChange={e => setTermsAccepted(e.target.checked)}
                    className='mt-0.5 h-4 w-4 shrink-0 cursor-pointer accent-ink'
                  />
                  <label htmlFor='terms' className='text-sm leading-snug'>
                    {t('terms.acceptTerms')}{' '}
                    <Link to='/bases' target='_blank' className='text-brand underline'>
                      {t('terms.viewTerms')}
                    </Link>
                  </label>
                </div>
                {submitted && errors.terms && (
                  <p className='text-xs text-red-500'>{errors.terms}</p>
                )}

                {/* Warning */}
                <div className='flex items-start gap-2 rounded-xl border border-yellow-200 bg-yellow-50 px-3 py-2.5'>
                  <AlertTriangle className='mt-0.5 h-4 w-4 shrink-0 text-yellow-600' />
                  <p className='text-xs text-yellow-800'>{t('reservation.verifyDataWarning')}</p>
                </div>

                {/* Switch to guest if signed-in user wants to change */}
                {accountMode === 'user' && (
                  <p className='text-xs text-zinc-400 text-center'>
                    {t('checkout.notYou', '¿No sos vos?')}{' '}
                    <button
                      type='button'
                      onClick={handleGuest}
                      className='underline text-zinc-500 hover:text-ink'
                    >
                      {t('checkout.continueAsGuest', 'Continuar como invitado')}
                    </button>
                  </p>
                )}
              </form>
            )}
          </div>
        </div>
      </div>

      {/* ─── Mobile sticky bar ───── */}
      {accountMode && (
        <div className='fixed bottom-0 left-0 right-0 z-30 border-t border-divider bg-white px-4 py-3 lg:hidden'>
          {submitButton()}
        </div>
      )}
    </div>
  );
}
